import React, { useState, useMemo } from "react";
import { FaTimes } from "react-icons/fa";
import { Table, BasicTable } from "../types";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (toTableId: string) => Promise<void>;
  fromTable: Table | null;
  availableTables: BasicTable[];
  isLoading?: boolean;
};

const MoveTableModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  fromTable,
  availableTables,
  isLoading = false,
}) => {
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const targetTables = useMemo(
    () => availableTables.filter((t) => t.id !== fromTable?.id),
    [availableTables, fromTable?.id]
  );

  if (!open || !fromTable) return null;

  const handleConfirm = async () => {
    setError("");

    if (!selectedTableId) {
      setError("Please select a target table");
      return;
    }

    const targetTable = targetTables.find((t) => t.id === selectedTableId);
    if (!targetTable) {
      setError("Invalid target table");
      return;
    }

    if (!targetTable.isAvailable) {
      setError("Target table must be available");
      return;
    }

    try {
      await onConfirm(selectedTableId);
      setSelectedTableId(null);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to move table");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm sm:max-w-md w-full">
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white">
            Move Order
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white disabled:opacity-50"
            aria-label="close modal"
          >
            <FaTimes size={16} className="sm:w-4.5 sm:h-4.5" />
          </button>
        </div>

        <div className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
          <div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 sm:mb-2">
              Move order from:
            </p>
            <div className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-gray-100 dark:bg-gray-700 font-semibold text-xs sm:text-sm text-gray-900 dark:text-white">
              {fromTable.name}
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-900 dark:text-white mb-1 sm:mb-2">
              Select Target Table *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 sm:gap-2">
              {targetTables.length > 0 ? (
                targetTables.map((table) => (
                  <button
                    key={table.id}
                    onClick={() => setSelectedTableId(table.id)}
                    disabled={!table.isAvailable || isLoading}
                    className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm transition-colors duration-200 ${
                      selectedTableId === table.id
                        ? "bg-orange-600 text-white ring-2 ring-orange-400"
                        : table.isAvailable
                        ? "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500"
                    }`}
                  >
                    {table.name}
                  </button>
                ))
              ) : (
                <p className="col-span-2 sm:col-span-3 text-center text-gray-500 dark:text-gray-400 py-2 sm:py-4 text-xs sm:text-sm">
                  No available tables
                </p>
              )}
            </div>
          </div>

          {error && (
            <div className="p-2 sm:p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-xs sm:text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex gap-2 sm:gap-3 pt-2 sm:pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm transition-colors duration-200 border border-gray-300 text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isLoading || !selectedTableId}
              className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm text-white bg-orange-600 hover:bg-orange-700 transition-colors duration-200 dark:bg-orange-600 dark:hover:bg-orange-700 disabled:opacity-50"
            >
              {isLoading ? "Moving..." : "Move"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoveTableModal;
